import { useState } from "react";
import { DockerContainerLogsResponse, DockerContainerStatusResponse, TradeAccountCredentials } from "../function/api";
import { useTranslation } from "react-i18next";
import { EyeIcon, EyeOffIcon } from "lucide-react";

type CredentialsField = keyof TradeAccountCredentials;

type CredentialsModalProps = {
    isOpen: boolean;
    values: TradeAccountCredentials;
    tradePlatform?: string;
    isLoading?: boolean;
    isSaving?: boolean;
    errorMessage?: string;
    onClose: () => void;
    onChange: (field: CredentialsField, value: string) => void;
    onSave: () => void;
};

type CredentialsConfirmationModalProps = {
    isOpen: boolean;
    title: string;
    description: string;
    actionLabel: string;
    onClose: () => void;
};

type ContainerStatusModalProps = {
    isOpen: boolean;
    statusData: DockerContainerStatusResponse | null;
    logsData?: DockerContainerLogsResponse | null;
    isLoading?: boolean;
    errorMessage?: string;
    onClose: () => void;
    onRestart: () => void;
};

const isObjectRecord = (value: unknown): value is Record<string, any> => {
    return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
};

const getEntries = (value: Record<string, any> | null | undefined) => {
    if (!value) {
        return [] as Array<[string, any]>;
    }

    return Object.entries(value).filter(([, entryValue]) => entryValue !== undefined);
};

const pickStatusMeta = (statusData: DockerContainerStatusResponse | null) => {
    if (!statusData) {
        return {};
    }

    const { container, status, ...rest } = statusData;
    return Object.fromEntries(
        Object.entries(rest).filter(([, value]) => value !== undefined && !isObjectRecord(value))
    );
};

const getLogDetails = (logsData?: DockerContainerLogsResponse | null) => {
    if (!isObjectRecord(logsData?.logs)) {
        return null;
    }

    if ('content' in logsData.logs || 'container' in logsData.logs || 'file_path' in logsData.logs) {
        return logsData.logs;
    }

    const normalizedPlatform = logsData?.platform?.toLowerCase();
    if (normalizedPlatform) {
        const platformLogs = logsData.logs[normalizedPlatform];
        if (isObjectRecord(platformLogs)) {
            return platformLogs;
        }
    }

    const firstLogEntry = Object.values(logsData.logs).find((entry) => isObjectRecord(entry));
    return isObjectRecord(firstLogEntry) ? firstLogEntry : null;
};

const MT4_SERVER_OPTIONS = [
    'ECMarkets-Demo',
    'ECMarkets-Live01',
    'ECMarkets-Live02',
    'ECMarkets-Live03',
    'ECMarkets-Live04',
    'ECMarkets-Live05',
    'ECMarkets-Live06',
    'ECMarkets-Live07',
    'ECMarkets-Live08',
    'ECMarkets-Live09',
    'ECMarkets-Live10',
    'ECMarkets-Live11',
    'ECMarkets-Live12',
    'ECMarkets-Live13',
    'ECMarketsLtd-Demo02',
    'ECMarketsGroupLtd-Demo',
    'ECMarketsGroupLtd-Live01',
    'FTMO-Demo2',
    'FTMO-Server',
    'FTMO-Server2',
    'FTMO-Server3',
    'FTMO-Server4',
    'MetaQuotes-Demo'
];

const MT5_SERVER_OPTIONS = [
    'ECMarketsLtd-Demo',
    'ECMarketsLtd-MT5-Live02',
    'ECMarketsLtd-MT5-Live03',
    'ECMarkets-MT5-Live01',
    'ECMarketsNZ-MT5-Live04',
    'FTMO-Demo',
    'FTMO-Demo2',
    'FTMO-Server',
    'FTMO-Server2',
    'FTMO-Server3',
    'FTMO-Server4',
    'FTMO-Server5',
    'MetaQuotes-Demo'
];

const inputClassName = "w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-sky-500 focus:bg-white focus:ring-4 focus:ring-sky-100 disabled:cursor-not-allowed disabled:opacity-60";

const CredentialsModal = ({
    isOpen,
    values,
    tradePlatform,
    isLoading = false,
    isSaving = false,
    errorMessage,
    onClose,
    onChange,
    onSave
}: CredentialsModalProps) => {
    const { t } = useTranslation();
    const [showPassword, setShowPassword] = useState(false);
    if (!isOpen) {
        return null;
    }

    const normalizedPlatform = (tradePlatform || values.platform).toLowerCase();
    const isCtrader = normalizedPlatform === 'ctrader';
    const serverOptions = normalizedPlatform === 'mt4'
        ? MT4_SERVER_OPTIONS
        : normalizedPlatform === 'mt5'
            ? MT5_SERVER_OPTIONS
            : [];
    const hasServerSelect = serverOptions.length > 0;
    const hasLegacyServerValue = Boolean(values.server) && !serverOptions.includes(values.server);
    const isBusy = isLoading || isSaving;

    const fieldLabelClass = "block text-xs mb-[5px]";
    const fieldInputClass = "w-full border rounded-lg px-3 py-2 text-sm outline-none transition-colors focus:border-[#00c9a7] disabled:cursor-not-allowed disabled:opacity-60";

    return (
        <div
            className="fixed inset-0 z-[10000] flex items-center justify-center px-4 py-6"
            style={{ background: 'rgba(10,18,30,0.55)' }}
            role="dialog"
            aria-modal="true"
            aria-labelledby="metrix-credentials-title"
            onClick={onClose}
        >
            <div
                className="w-full max-w-[600px] overflow-hidden rounded-xl bg-white shadow-2xl"
                style={{ border: '0.5px solid #e0e0e0' }}
                onClick={(event) => event.stopPropagation()}
            >
                {/* Header */}
                <div
                    className="flex items-center justify-between px-5 py-[1.1rem]"
                    style={{ background: '#0b1c2e' }}
                >
                    <div>
                        <p id="metrix-credentials-title" className="m-0 text-[15px] font-medium text-white">
                            {t('Trading Account Credentials')}
                        </p>
                        <p className="m-0 mt-[3px] text-xs" style={{ color: '#8a9db5' }}>
                            {t('Enter your account connection details below.')}
                        </p>
                    </div>
                    <button
                        type="button"
                        className="text-[22px] leading-none transition hover:text-white"
                        style={{ background: 'none', border: 'none', color: '#8a9db5', cursor: 'pointer', padding: 0 }}
                        aria-label={t('Close')}
                        onClick={onClose}
                    >
                        ×
                    </button>
                </div>

                {/* Body */}
                <div className="flex flex-col gap-3 px-5 py-[1.1rem]">
                    <div className="grid grid-cols-2 gap-[10px]">
                        <div>
                            <label style={{ color: '#8a9db5' }} className={fieldLabelClass}>{t('Account')}</label>
                            <input
                                type="text"
                                className={fieldInputClass}
                                readOnly
                                style={{ borderColor: '#dde1e8', color: '#0b1c2e', background: '#f0f0f0' }}
                                value={values.account}
                                onChange={(event) => onChange('account', event.target.value)}
                                placeholder={t('Enter account number')}
                                disabled
                            />
                        </div>
                        <div>
                            <label style={{ color: '#8a9db5' }} className={fieldLabelClass}>{t('Platform')}</label>
                            <div
                                className="flex items-center rounded-lg border px-3 py-2 h-[42px]"
                                style={{ borderColor: '#dde1e8', background: '#f9fafb' }}
                            >
                                <span
                                    className="rounded px-2 py-0.5 text-xs font-medium tracking-wider"
                                    style={{ color: '#00c9a7' }}
                                >
                                    #{values.platform.toUpperCase() || normalizedPlatform.toUpperCase()}
                                </span>
                            </div>
                        </div>

                    </div>

                    <div className="grid grid-cols-2 gap-[10px]">
                        {!isCtrader && (
                            <div>
                                <label style={{ color: '#8a9db5' }} className={fieldLabelClass}>{t('Server')}</label>
                                {hasServerSelect ? (
                                    <select
                                        className={fieldInputClass}
                                        style={{ borderColor: '#dde1e8', color: '#0b1c2e', background: '#ffffff', cursor: 'pointer', appearance: 'none', backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%23b0b8c4' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E\")", backgroundRepeat: 'no-repeat', backgroundPosition: 'right 10px center' }}
                                        value={values.server}
                                        onChange={(event) => onChange('server', event.target.value)}
                                        disabled={isBusy}
                                    >
                                        <option value="">{t('Select server')}</option>
                                        {hasLegacyServerValue && (
                                            <option value={values.server}>{values.server}</option>
                                        )}
                                        {serverOptions.map((server) => (
                                            <option key={server} value={server}>{server}</option>
                                        ))}
                                    </select>
                                ) : (
                                    <input
                                        type="text"
                                        className={fieldInputClass}
                                        style={{ borderColor: '#dde1e8', color: '#0b1c2e', background: '#ffffff' }}
                                        value={values.server}
                                        onChange={(event) => onChange('server', event.target.value)}
                                        placeholder={t('Enter server name')}
                                        disabled={isBusy}
                                    />
                                )}
                            </div>
                        )}



                        {isCtrader && (
                            <div>
                                <label style={{ color: '#8a9db5' }} className={fieldLabelClass}>{t('Lumir_bot.cid')}</label>
                                <input
                                    type="text"
                                    className={fieldInputClass}
                                    style={{ borderColor: '#dde1e8', color: '#0b1c2e', background: '#ffffff' }}
                                    value={values.ctraderId}
                                    onChange={(event) => onChange('ctraderId', event.target.value)}
                                    placeholder={t('Lumir_bot.enter_cid')}
                                    disabled={isBusy}
                                />
                            </div>
                        )}

                        <div style={{ position: 'relative' }}> {/* Thêm relative để position icon chính xác */}
                            <label style={{ color: '#8a9db5' }} className={fieldLabelClass}>{t('Lumir_bot.password')}</label>

                            <input
                                type={showPassword ? "text" : "password"} // Thay đổi type động
                                className={fieldInputClass}
                                style={{
                                    borderColor: '#dde1e8',
                                    color: '#0b1c2e',
                                    background: '#ffffff',
                                    paddingRight: '40px' // Chừa khoảng trống bên phải để không bị chữ đè lên icon
                                }}
                                value={values.password}
                                onChange={(event) => onChange('password', event.target.value)}
                                placeholder={t('Enter password')}
                                disabled={isBusy}
                            />

                            {/* Button Icon con mắt */}
                            <button
                                type="button" // Tránh việc submit form ngoài ý muốn khi click
                                onClick={() => setShowPassword(!showPassword)}
                                style={{
                                    position: 'absolute',
                                    right: '10px',
                                    bottom: '8px', // Bạn tự căn chỉnh lại cho vừa vặn với chiều cao input
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    fontSize: '16px',
                                    outline: 'none'
                                }}
                            >
                                {showPassword ? <EyeOffIcon size={24} /> : <EyeIcon size={24} />} {/* Thay bằng thẻ <Icon /> của bạn ở đây */}
                            </button>
                        </div>
                    </div>

                    {errorMessage && (
                        <div className="rounded-lg border px-4 py-3 text-xs" style={{ background: '#fff3f3', borderColor: '#f5c1c1', color: '#a32d2d' }}>
                            {errorMessage}
                        </div>
                    )}

                    {isLoading && (
                        <div className="rounded-lg border px-4 py-3 text-xs" style={{ borderColor: '#dde1e8', background: '#f9fafb', color: '#8a9db5' }}>
                            {t('Loading account credentials...')}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div
                    className="flex items-center justify-end gap-2 px-5 py-[0.85rem]"
                    style={{ background: '#f5f7fa', borderTop: '0.5px solid #e0e0e0' }}
                >
                    <button
                        type="button"
                        className="!rounded-[8px] px-[18px] py-[7px] text-sm font-medium transition hover:bg-[#eef1f5] disabled:cursor-not-allowed disabled:opacity-60"
                        style={{ border: '1px solid #cdd5e0', background: '#ffffff', color: '#0b1c2e', cursor: 'pointer' }}
                        onClick={onClose}
                        disabled={isBusy}
                    >
                        {t('Cancel')}
                    </button>
                    <button
                        type="button"
                        className="!rounded-[8px] px-[18px] py-[7px] text-sm font-medium transition hover:bg-[#00b496] disabled:cursor-not-allowed disabled:opacity-60"
                        style={{ background: '#00c9a7', color: '#0b1c2e', border: 'none', cursor: 'pointer' }}
                        onClick={onSave}
                        disabled={isBusy}
                    >
                        {isSaving ? t('Connecting') + '...' : t('Connect')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CredentialsModal;

export const ContainerStatusModal = ({
    isOpen,
    statusData,
    logsData,
    isLoading = false,
    errorMessage,
    onClose,
    onRestart
}: ContainerStatusModalProps) => {
    const { t } = useTranslation();

    if (!isOpen) {
        return null;
    }

    const statusMetaEntries = getEntries(pickStatusMeta(statusData));
    const containerEntries = getEntries(isObjectRecord(statusData?.container) ? statusData.container : undefined);
    const statusEntries = getEntries(isObjectRecord(statusData?.status) ? statusData.status : undefined);
    const logDetails = getLogDetails(logsData);
    const logContent = logDetails?.content?.trim();
    const logSource = logDetails?.container || logDetails?.file_path || statusData?.container?.container_name || statusData?.account || '-';
    const logFileDetails = [logDetails?.directory, logDetails?.file_path].filter(Boolean).join(' / ');

    const account = statusData?.account || statusData?.container?.account;
    const platform = (logsData?.platform || statusData?.platform || statusData?.container?.platform || '').toUpperCase();

    const HIDDEN_KEYS = new Set([
        'container_name', 'Command', 'CreatedAt', 'Image', 'Labels', 'ID',
        'LocalVolumes', 'Mounts', 'Names', 'Networks', 'Platform', 'Ports', 'RunningFor'
    ]);
    const allInfoEntries = [...statusMetaEntries, ...containerEntries, ...statusEntries]
        .filter(([key]) => !HIDDEN_KEYS.has(key));

    const getStatusColor = (key: string, value: string) => {
        const lowerKey = key.toLowerCase();
        const lowerVal = value.toLowerCase();
        if (lowerKey.includes('state') || lowerKey.includes('status')) {
            if (lowerVal.includes('exit') || lowerVal.includes('stop')) return '#d64545';
            if (lowerVal.includes('run') || lowerVal.includes('active') || lowerVal.includes('up')) return '#00c9a7';
        }
        return null;
    };

    return (
        <div
            className="fixed inset-0 z-[10001] flex items-center justify-center px-4 py-6"
            style={{ background: 'rgba(10,18,30,0.7)' }}
            role="dialog"
            aria-modal="true"
            aria-labelledby="metrix-container-status-title"
            onClick={onClose}
        >
            <div
                className="w-full max-w-xl overflow-hidden rounded-xl bg-white shadow-2xl"
                style={{ border: '0.5px solid #e0e0e0' }}
                onClick={(event) => event.stopPropagation()}
            >
                {/* Header */}
                <div
                    className="flex items-center justify-between px-6 py-4"
                    style={{ background: '#0b1c2e' }}
                >
                    <div>
                        <p id="metrix-container-status-title" className="m-0 text-sm font-medium text-white">
                            {t('Trading Bot Status')}
                        </p>
                        <p className="m-0 mt-1 flex items-center gap-2 text-xs" style={{ color: '#00c9a7' }}>
                            {account && <span>{t('Account')} {account}</span>}
                            {account && platform && <span>·</span>}
                            {platform && (
                                <span
                                    className="rounded px-2 py-0.5 text-xs font-medium"
                                    style={{ background: '#0f2d4a', color: '#00c9a7' }}
                                >
                                    #{platform}
                                </span>
                            )}
                            {
                                statusData?.container?.container_name && (
                                    <span
                                        className="rounded px-2 py-0.5 text-xs font-medium"
                                        style={{ background: '#0f2d4a', color: '#00c9a7' }}
                                    >
                                        {statusData.container.container_name}
                                    </span>
                                )
                            }
                        </p>
                    </div>
                    <button
                        type="button"
                        className="text-2xl leading-none transition"
                        style={{ background: 'none', border: 'none', color: '#8a9db5', cursor: 'pointer', padding: 0 }}
                        aria-label={t('Close')}
                        onClick={onClose}
                    >
                        ×
                    </button>
                </div>

                {/* Scrollable body */}
                <div className="overflow-y-auto" style={{ maxHeight: 'calc(75vh - 120px)' }}>
                    {/* Warning / error banner */}
                    {errorMessage && (
                        <div
                            className="mx-5 mt-4 flex items-start gap-2.5 rounded-lg px-4 py-3"
                            style={{ background: '#fff3f3', border: '0.5px solid #f5c1c1' }}
                        >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ color: '#d64545', flexShrink: 0, marginTop: 1 }}>
                                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                                <line x1="12" y1="8" x2="12" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                <circle cx="12" cy="16" r="1" fill="currentColor" />
                            </svg>
                            <div>
                                <p className="m-0 text-sm font-medium" style={{ color: '#a32d2d' }}>{t('Bot has stopped')}</p>
                                <p className="m-0 mt-0.5 text-xs" style={{ color: '#a32d2d', opacity: 0.85 }}>{errorMessage}</p>
                            </div>
                        </div>
                    )}

                    {isLoading ? (
                        <div className="m-5 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
                            {t('Loading container details...')}
                        </div>
                    ) : (
                        <div className="p-5 space-y-5">
                            {/* Connection info grid */}
                            {allInfoEntries.length > 0 && (
                                <div>
                                    <p
                                        className="mb-2.5 text-xs font-semibold uppercase tracking-widest"
                                        style={{ color: '#8a9db5' }}
                                    >
                                        {t('Connection Info')}
                                    </p>
                                    <div className="grid grid-cols-2 gap-2">
                                        {allInfoEntries.map(([key, value]) => {
                                            const strVal = typeof value === 'object' ? JSON.stringify(value) : String(value ?? '-');
                                            const statusColor = getStatusColor(key, strVal);
                                            const isStopped = statusColor === '#d64545';
                                            const isRunning = statusColor === '#00c9a7';
                                            return (
                                                <div key={key} className="rounded-lg px-3 py-2.5" style={{ background: '#f5f7fa' }}>
                                                    <p className="m-0 mb-1 text-xs" style={{ color: '#8a9db5' }}>{t(key)}</p>
                                                    <p
                                                        className="m-0 flex items-center gap-1.5 break-all text-sm font-medium"
                                                        style={{ color: statusColor ?? '#0b1c2e' }}
                                                    >
                                                        {(isStopped || isRunning) && (
                                                            <span
                                                                className="inline-block h-1.5 w-1.5 flex-shrink-0 rounded-full"
                                                                style={{ background: statusColor! }}
                                                            />
                                                        )}
                                                        {strVal}
                                                    </p>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* Logs panel */}
                            {/* <div className="overflow-hidden rounded-xl" style={{ background: '#0b1c2e' }}>
                                <div
                                    className="flex items-center justify-between px-4 py-3"
                                    style={{ borderBottom: '0.5px solid rgba(255,255,255,0.08)' }}
                                >
                                    <div>
                                        <p className="m-0 text-xs font-semibold uppercase tracking-widest" style={{ color: '#8fb3d5' }}>
                                            {t('Recent Logs')}
                                        </p>
                                        <p className="m-0 mt-0.5 text-xs" style={{ color: '#5a7a9a' }}>{logSource}</p>
                                        {logFileDetails && (
                                            <p className="m-0 mt-0.5 break-all text-[11px]" style={{ color: '#3d5a75' }}>{logFileDetails}</p>
                                        )}
                                    </div>
                                    <span
                                        className="rounded-full px-3 py-1 text-xs"
                                        style={{ border: '1px solid rgba(255,255,255,0.1)', color: '#8fb3d5' }}
                                    >
                                        {t('Last 10 lines')}
                                    </span>
                                </div>
                                <pre className="m-0 max-h-64 overflow-auto whitespace-pre-wrap break-words p-4 text-xs leading-6" style={{ color: '#c8dde8' }}>
                                    {logContent || t('No logs available.')}
                                </pre>
                            </div> */}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div
                    className="flex items-center justify-end gap-2 px-5 py-3.5"
                    style={{ background: '#f5f7fa', borderTop: '0.5px solid #e0e0e0' }}
                >
                    <button
                        type="button"
                        className="!rounded-lg inline-flex items-center justify-center px-4 py-2 text-sm font-medium transition hover:bg-slate-100"
                        style={{ border: '1px solid #cdd5e0', background: '#ffffff', color: '#0b1c2e', cursor: 'pointer' }}
                        onClick={onClose}
                    >
                        {t('Close')}
                    </button>
                    <button
                        type="button"
                        className="!rounded-lg inline-flex items-center justify-center gap-1.5 px-4 py-2 text-sm font-medium transition"
                        style={{ background: '#00c9a7', color: '#0b1c2e', border: 'none', cursor: 'pointer' }}
                        onClick={onRestart}
                    >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M1 4v6h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M3.51 15a9 9 0 1 0 .49-4.95L1 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        {t('Restart bot')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export const CredentialsConfirmationModal = ({
    isOpen,
    title,
    description,
    actionLabel,
    onClose
}: CredentialsConfirmationModalProps) => {
    if (!isOpen) {
        return null;
    }

    return (
        <div
            className="fixed inset-0 z-[10001] flex items-center justify-center bg-slate-950/60 px-4 py-6"
            role="dialog"
            aria-modal="true"
            aria-labelledby="metrix-credentials-confirmation-title"
            onClick={onClose}
        >
            <div
                className="w-full max-w-md rounded-3xl bg-white p-6 shadow-2xl shadow-slate-950/20 sm:p-8"
                onClick={(event) => event.stopPropagation()}
            >
                <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </div>
                <div className="mt-5 text-center">
                    <h3 id="metrix-credentials-confirmation-title" className="text-2xl font-semibold text-slate-900">
                        {title}
                    </h3>
                    <p className="mt-2 text-sm text-slate-500">
                        {description}
                    </p>
                </div>
                <div className="mt-6 flex justify-center">
                    <button
                        type="button"
                        className="inline-flex items-center justify-center rounded-xl bg-sky-500 px-5 py-3 text-sm font-semibold text-white transition hover:bg-sky-600"
                        onClick={onClose}
                    >
                        {actionLabel}
                    </button>
                </div>
            </div>
        </div>
    );
};